import React, { FC, useEffect, useRef, useState} from 'react'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import { refreshPlatformDataAsync} from 'src/redux/actions/platformActions'
import { PlatformDataType } from 'src/interfaces/Platform.interface'
import useAppToast from 'src/hooks/useAppToast'
import Loader from 'src/utils/Loader'
import {Link} from 'react-router-dom'
import TemaplateComponent from './components/TemaplateComponent.previous'
import { KTSVG } from 'src/marketConnect/helpers'
import CommonAlertModal from 'src/utils/modals/CommonAlertModal'
import clsx from 'clsx'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import {
  getConnectedPlatformCountryCode,
  getConnectedPlatformLabel,
  getConnectedPlatformMonogram,
  getConnectedPlatformTooltip,
} from './utils/connectedPlatformLabel.previous'
import './styles/product-listing-previous.scss'

const MIGRATION_GUIDE_URL =
  'https://commercium.tawk.help/article/listing-rules-simplify-your-product-management'

const LEGACY_FLOW_HINT_DISMISSED_KEY = 'listing_profiles_legacy_flow_hint_dismissed'

const ProductListingFlowToggle: FC<{
  isLegacyMode: boolean
  onToggle: () => void
}> = ({ isLegacyMode, onToggle }) => {
  const [hintDismissed, setHintDismissed] = useState(
    () => localStorage.getItem(LEGACY_FLOW_HINT_DISMISSED_KEY) === 'true'
  )

  const showLegacyHint = !isLegacyMode && !hintDismissed

  const dismissLegacyHint = () => {
    localStorage.setItem(LEGACY_FLOW_HINT_DISMISSED_KEY, 'true')
    setHintDismissed(true)
  }

  return (
    <div className="product-listing__flow-toggle-cluster">
      {showLegacyHint && (
        <div className="product-listing__legacy-hint" role="status">
          <div className="product-listing__legacy-hint-bubble">
            <i className="bi bi-info-circle-fill product-listing__legacy-hint-icon" aria-hidden="true" />
            <span className="product-listing__legacy-hint-text">
              Looking for your old rules? Click here!
            </span>
            <button
              type="button"
              className="product-listing__legacy-hint-close"
              onClick={dismissLegacyHint}
              aria-label="Dismiss legacy navigation hint"
            >
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
      <div className="product-listing__flow-toggle">
        <span className="product-listing__flow-toggle-label">
          {isLegacyMode ? 'Using Legacy Flow' : 'Using New Flow'}
        </span>
        <span className="product-listing__flow-toggle-divider" aria-hidden="true" />
        <button
          type="button"
          className={clsx(
            'product-listing__flow-toggle-link',
            showLegacyHint && 'product-listing__flow-toggle-link--highlighted'
          )}
          onClick={onToggle}
        >
          {isLegacyMode ? 'Back to Listing Profiles' : 'View Legacy Rules & Plan'}
          <i className="bi bi-chevron-right" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}

const LegacyModeBanner: FC = () => (
  <div className="product-listing__legacy-banner" role="status">
    <i className="bi bi-exclamation-circle product-listing__legacy-banner-icon" aria-hidden="true" />
    <div>
      <p className="product-listing__legacy-banner-title">Legacy Mode Active</p>
      <p className="product-listing__legacy-banner-text">
        You are viewing your original Listing Rules and Plans.{' '}
        <strong>Listing Profiles</strong> are recommended for better performance.
      </p>
      <a
        href={MIGRATION_GUIDE_URL}
        target="_blank"
        rel="noreferrer"
        className="product-listing__legacy-banner-link"
      >
        Read Migration Guide
        <i className="bi bi-box-arrow-up-right fs-8" aria-hidden="true" />
      </a>
    </div>
  </div>
)

const hasSourceStoreFlag = (platform: any) =>
  Object.entries(platform ?? {}).some(
    ([key, value]) =>
      key !== 'isConnected' &&
      key !== 'isActive' &&
      key !== 'isRequired' &&
      /^is[A-Z].*Store$/.test(key) &&
      value === true
  )

const isSecondaryPlatform = (platform: any) =>
  Boolean(platform) && !hasSourceStoreFlag(platform)

const ListingProfilesPreviousPage: FC = () => {
  const refreshDurationMessage = 'This might take 15-20 minutes to complete'
  const refreshSubmittedMessage = 'Request submitted. This might take 15-20 mins to complete'

  const [showRefreshModal, setShowRefreshModal] = useState<boolean>(false)
  const [refreshPlatformId, setRefreshPlatformId] = useState<number|null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [currentActiveTab, setCurrentActiveTab] = useState(1)
  const [currentActivePlatform, setCurrentActivePlatform] = useState<number|undefined>(undefined)
  const [isLegacyMode, setIsLegacyMode] = useState(false)
  const {fetchedConnectedPlatformData, fetchedConnectedPlatformLoading} = useAppSelector(
    (state) => state.fetchConnectedPlatform
  )
  const [secondaryStorePlatforms, setSecondaryStorePlatforms] = useState<PlatformDataType[]>([])
  const tabScrollRef = useRef<HTMLDivElement>(null)
  const [canScrollTabs, setCanScrollTabs] = useState(false)
  const [canScrollTabsLeft, setCanScrollTabsLeft] = useState(false)
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false)
  const { successToast } = useAppToast()
  const dispatch = useAppDispatch()
  const {
    platformPricingTemplateLoading,
  } = useAppSelector((state) => state.platformRepricing)
  const {  fetchedUserAccountData } = useAppSelector(state=>state.userAccountData)

  useEffect(() => {
    if (fetchedConnectedPlatformData && fetchedConnectedPlatformData.length > 0) {
      const filteredSecondaryStore: any = fetchedConnectedPlatformData.filter(isSecondaryPlatform)
      setCurrentActivePlatform(filteredSecondaryStore?.[0]?.id);
      setSecondaryStorePlatforms(filteredSecondaryStore);
    } else {
      setCurrentActivePlatform(undefined);
      setSecondaryStorePlatforms([]);
    }
  }, [fetchedConnectedPlatformData])

  useEffect(() => {
    const el = tabScrollRef.current

    if (!el) {
      setCanScrollTabs(false)
      setCanScrollTabsLeft(false)
      setCanScrollTabsRight(false)
      return
    }

    const updateScrollState = () => {
      const hasOverflow = el.scrollWidth > el.clientWidth + 2

      setCanScrollTabs(hasOverflow)
      setCanScrollTabsLeft(el.scrollLeft > 1)
      setCanScrollTabsRight(
        el.scrollLeft + el.clientWidth < el.scrollWidth - 1
      )
    }

    updateScrollState()

    const observer = new ResizeObserver(updateScrollState)
    observer.observe(el)

    el.addEventListener('scroll', updateScrollState, {passive: true})

    return () => {
      observer.disconnect()
      el.removeEventListener('scroll', updateScrollState)
    }
  }, [secondaryStorePlatforms])

  const scrollPlatformTabs = (direction: -1 | 1) => {
    tabScrollRef.current?.scrollBy({left: direction * 180, behavior: 'smooth'})
  }

  return (
    <div className="listing-profiles-previous-root product-listing">
      <div className="product-listing__page-header">
        <div>
          <h1 className="product-listing__page-title">List Products</h1>
          <p className="product-listing__page-subtitle">
            Configure Listing profiles to automate product filtering, field mapping, pricing adjustments, and marketplace synchronization across your connected platforms.
          </p>
        </div>
        {secondaryStorePlatforms.length > 0 && (
          <ProductListingFlowToggle
            isLegacyMode={isLegacyMode}
            onToggle={() => setIsLegacyMode((prev) => !prev)}
          />
        )}
      </div>
      {/* <ul className='breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 mt-2'>
        <li className='breadcrumb-item'>
          <span className='bullet bg-gray-500 w-5px h-2px'></span>
        </li>
        <li className='breadcrumb-item px-3 text-muted text-hover-primary'>Template and Pricing</li>
      </ul> */}

      <div className='py-2 '>
        <div className={ secondaryStorePlatforms.length==1 ? '' : 'px-md-7 px-4  py-2 shadow'}>
          {fetchedConnectedPlatformLoading ? (
            <div className='d-flex align-items-center justify-content-center h-100'>
              <h1>
                <Loader variant="section" />
              </h1>
            </div>
          ) : (
            <div>
              {secondaryStorePlatforms.length ? (
              <div className="product-listing__channel-tabs">
                <div className="product-listing__channel-tabs-scroll" ref={tabScrollRef} role="tablist">
                {secondaryStorePlatforms.map((item,index) => {
                    const isActive = currentActiveTab === index + 1
                    const platformLabel = getConnectedPlatformLabel(item)
                    const platformTooltip = getConnectedPlatformTooltip(item)
                    const countryCode = getConnectedPlatformCountryCode(item)
                    const monogram = getConnectedPlatformMonogram(item)

                    return(
                        <OverlayTrigger
                          key={item.id ?? `tab-items-${index}`}
                          placement="top"
                          delay={{ show: 180, hide: 0 }}
                          container={typeof document !== 'undefined' ? document.body : undefined}
                          overlay={
                            <Tooltip id={`listing-platform-tab-${item.id ?? index}`}>
                              {platformTooltip}
                            </Tooltip>
                          }
                        >
                          <button
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            aria-label={platformTooltip}
                            onClick={()=>{setCurrentActiveTab(index+1);setCurrentActivePlatform(item?.id)}}
                            className={clsx(
                              'product-listing__channel-tab',
                              isActive && 'product-listing__channel-tab--active'
                            )}
                          >
                            {item.platform_info?.logo_ref ? (
                              <img
                                src={item.platform_info.logo_ref}
                                className="product-listing__channel-tab-logo"
                                alt=""
                              />
                            ) : (
                              <span className="product-listing__channel-tab-monogram" aria-hidden="true">
                                {monogram}
                              </span>
                            )}
                            <span className="product-listing__channel-tab-copy">
                              {countryCode ? (
                                <span className="product-listing__channel-tab-country">{countryCode}</span>
                              ) : null}
                              <span className="product-listing__channel-tab-name">{platformLabel}</span>
                            </span>
                          </button>
                        </OverlayTrigger>
                    )
            })}
                </div>
                {canScrollTabs && (
                  <div className="product-listing__channel-tabs-nav">
                   <button
                      type="button"
                      className="product-listing__channel-tabs-nav-btn"
                      onClick={() => scrollPlatformTabs(-1)}
                      disabled={!canScrollTabsLeft}
                      aria-label="Scroll platforms backward"
                    >
                      <i className="bi bi-chevron-left" />
                    </button>
                    <button
                      type="button"
                      className="product-listing__channel-tabs-nav-btn"
                      onClick={() => scrollPlatformTabs(1)}
                      disabled={!canScrollTabsRight}
                      aria-label="Scroll platforms forward"
                    >
                      <i className="bi bi-chevron-right" />
                    </button>
                  </div>
                )}
              </div>
              ) : (
              <div className="w-100 alert bg-light-primary d-flex align-items-center p-5 shadow-sm">
                <span className="svg-icon svg-icon-2hx svg-icon-primary me-8">
                  <KTSVG path="media/icons/duotune/communication/com009.svg" className="svg-icon-muted svg-icon-3hx" />
                </span>
          
                <div className="d-flex flex-column">
                  <h5 className="mb-1">No Rules and Plans yet </h5>
                  <span>Please connect a platform. Click here to connect <Link to={'/dashboard'} > <KTSVG path="media/icons/duotune/general/gen016.svg" className="svg-icon-primary svg-icon-hx" /></Link>  </span>
                </div>
              </div>
              )}
              {isLegacyMode && <LegacyModeBanner />}
              <div
                className={clsx('tab-content', !isLegacyMode && 'product-listing__tab-content')}
                id="myTabContent"
              >
                  {secondaryStorePlatforms.length ? 
                      secondaryStorePlatforms.map((item,index) => {
                          return(
                            <div
                              key={'tab_data'+index}
                                className={clsx("tab-pane", currentActiveTab===(index+1) ? "active show customTabBody shadow-sm" : '' )}
                                // id={"kt_tab_pane_"+(index+1)}
                                // role={"tabpanel"}
                              > 
                                 <TemaplateComponent
                                    key={item.display_name}
                                    item={item}
                                    platformPricingTemplateLoading={platformPricingTemplateLoading}
                                    setShowRefreshModal={setShowRefreshModal}
                                    setRefreshPlatformId={setRefreshPlatformId}
                                    currentActivePlatform={currentActivePlatform}
                                    isLegacyMode={isLegacyMode}
                                  />
                              </div>
                          )
                      }) : 
                      null 
                  }
              </div>
            </div>
          )}
        </div>
      </div>
      <CommonAlertModal
        key={'reffresh data modal'}
        isVisible={showRefreshModal}
        successDisabled={refreshing}
        onCancel={()=>{
          setShowRefreshModal(false)
          setRefreshPlatformId(null)
        }}
        onSuccess={()=>{
          if(refreshPlatformId){
            setRefreshing(true)
            dispatch(refreshPlatformDataAsync({platformId:refreshPlatformId}))
            .then(()=>{
              successToast(refreshSubmittedMessage)
              setRefreshing(false)
              setRefreshPlatformId(null)
              setShowRefreshModal(false)
            })
          }
        }}
        title='Are you sure ?'
        subtitle={refreshDurationMessage}
        isTermsVisible={false}
      />
    </div>
  )
}
export default ListingProfilesPreviousPage
